import { CreateCharForm } from '@/store/create'
import { DraftSource } from 'Types/draft'

export const WEB_DRAFT_FIELDS: Array<keyof CreateCharForm> = [
  'test_chat_tcid',
  'createImgStep',
  'uploadImgReferencePreviewUrl',
  'uploadImgReferencePath',
  'uploadGifReferencePreviewUrl',
  'uploadGifReferencePath',
  'showUploadGif',
  'generateImgReferencePreviewUrl',
  'generateImgReferencePath',
  'generateImgReferenceUrl',
  'generatedNum',
  'imgFaceReferenceCroppedArea',
  'imgFaceReferenceCroppedAreaPixels',
  'isMimicFace',
  'isMimicStyle',
  'isMimicImage',
  'imgPrompt',
  'imgStyleKey',
  'imgStylesKey',
  'uploadImgType',
  'uploadUploadImageKey',
  'uploadUploadImageUrl',
  'uploadUpscaleImageKey',
  'uploadUpscaleImageUrl',
  'uploadVaryImageKey',
  'uploadVaryImageUrl',
  'uploadGifKey',
  'uploadGifUrl',
  'croppedArea',
  'croppedAreaPixels',
  'generatingImgData',
  'charImgSourcePath',
  'sourceImgReferencePreviewUrl',
  'sourceImgPrompt',
  'charImageUrl',
  'charImagePath',
  'charPcImageUrl',
  'charPcImagePath',
  'charFaceImageCroppedArea',
  'charFaceImageCroppedAreaPixels',
  'charFaceImagePreviewUrl',
  'charFaceImagePath',
  'nsfwCheck',
  'charNickname',
  'charGender',
  'character_type',
  'role_character_ids',
  'role_character_list',
  'plot_file_key',
  'plot_file_name',
  'plot_file_prefix',
  'is_plot_file_uploading',
  // 与 plot_file_key 一同持久化：语义同 world_books_loaded*（用户改动为权威 + update 三态提交依据）
  'plot_file_loaded',
  'plot_file_loaded_character_id',
  'world_books',
  // 与 world_books 一同持久化：恢复草稿后保持「该角色列表为权威」，
  // 避免 echo 因标记丢失而用服务端绑定覆盖草稿里的世界书改动。
  'world_books_loaded',
  'world_books_loaded_character_id',
  'charShortDescribe',
  'charLongDescribe',
  'showCharLongDescribe',
  'contentType',
  'charIsPublic',
  'charTagIDs',
  'charScenario',
  'charGreeting',
  'advancedSetCheck',
  'conversationStyleKey',
  'charDialogStyle',
  'optimizationCode',
  'outputFormatConfig',
  'replyRequirement',
  'charBaseTone',
  'charVoiceSpeed',
  'greetingVideoKey',
  'greetingVideoUrl',
]

export type WebDraftPayload = {
  draft_source: DraftSource
} & Record<string, unknown>

const isHttpUrl = (value: unknown): value is string =>
  typeof value === 'string' && /^https?:\/\//i.test(value)

const normalizeStringArray = (value: unknown): string[] | undefined => {
  if (Array.isArray(value) === false) return undefined
  return value.filter((item): item is string => typeof item === 'string')
}

const normalizeCharacterType = (value: unknown): 1 | 2 | undefined => {
  if (value === 1 || value === 2) return value
  return undefined
}

const normalizeIdList = (
  value: unknown
):
  | Array<{ character_id: string; nickname: string; image_url: string }>
  | undefined => {
  if (Array.isArray(value) === false) return undefined
  return value
    .map((item) => {
      if (item == null || typeof item !== 'object') return null
      const data = item as Record<string, unknown>
      if (
        typeof data.character_id !== 'string' ||
        typeof data.nickname !== 'string' ||
        typeof data.image_url !== 'string'
      ) {
        return null
      }
      return {
        character_id: data.character_id,
        nickname: data.nickname,
        image_url: data.image_url,
      }
    })
    .filter(
      (
        item
      ): item is {
        character_id: string
        nickname: string
        image_url: string
      } => !!item
    )
}

const normalizeWorldBooks = (
  value: unknown
): Array<{ key: string; name: string; entry_count?: number }> => {
  if (Array.isArray(value) === false) return []
  return value
    .map((item) => {
      if (item == null || typeof item !== 'object') return null
      const data = item as Record<string, unknown>
      if (typeof data.key !== 'string') return null
      return {
        key: data.key,
        name: typeof data.name === 'string' ? data.name : '',
        // 条目数仅用于展示「已识别到 N 条」，随草稿一同恢复
        ...(typeof data.entry_count === 'number'
          ? { entry_count: data.entry_count }
          : null),
      }
    })
    .filter(
      (item): item is { key: string; name: string; entry_count?: number } =>
        !!item
    )
}

const normalizeUrlForDraft = (url: unknown): unknown => {
  // 只接受后端回传的远程 URL；临时 data/blob/file URL 一律清空。
  if (isHttpUrl(url)) return url
  if (typeof url === 'string') return ''
  return url ?? ''
}

export function buildWebDraftPayload(state: CreateCharForm): WebDraftPayload {
  const payload: WebDraftPayload = { draft_source: 'web' }
  WEB_DRAFT_FIELDS.forEach((key) => {
    payload[key] = state[key]
  })

  payload.characterType = state.character_type
  if (state.character_type === 2) {
    payload.character_ids = state.role_character_ids || []
  }
  // 剧本单/多角色均支持：cross-end camelCase 别名不再按类型区分（此前仅 type=2 输出）
  payload.plotFileKey = state.plot_file_key || ''
  payload.plotFileName = state.plot_file_name || ''
  payload.plotFilePrefix = state.plot_file_prefix || ''
  payload.isPlotFileUploading = Boolean(state.is_plot_file_uploading)

  const draftUploadImageUrl = normalizeUrlForDraft(state.draftUploadImageUrl)

  payload.charImageUrl =
    draftUploadImageUrl || normalizeUrlForDraft(payload.charImageUrl)
  payload.charPcImageUrl = normalizeUrlForDraft(payload.charPcImageUrl)
  payload.uploadUploadImageUrl =
    draftUploadImageUrl || normalizeUrlForDraft(payload.uploadUploadImageUrl)
  payload.uploadUpscaleImageUrl = normalizeUrlForDraft(
    payload.uploadUpscaleImageUrl
  )
  payload.uploadVaryImageUrl = normalizeUrlForDraft(payload.uploadVaryImageUrl)
  payload.uploadGifUrl = normalizeUrlForDraft(payload.uploadGifUrl)
  payload.greetingVideoUrl = normalizeUrlForDraft(payload.greetingVideoUrl)
  payload.charFaceImagePreviewUrl = normalizeUrlForDraft(
    payload.charFaceImagePreviewUrl
  )

  payload.uploadImgReferencePreviewUrl = normalizeUrlForDraft(
    payload.uploadImgReferencePreviewUrl
  )
  payload.uploadGifReferencePreviewUrl = normalizeUrlForDraft(
    payload.uploadGifReferencePreviewUrl
  )
  payload.generateImgReferencePreviewUrl = normalizeUrlForDraft(
    payload.generateImgReferencePreviewUrl
  )
  payload.sourceImgReferencePreviewUrl = normalizeUrlForDraft(
    payload.sourceImgReferencePreviewUrl
  )
  payload.generateImgReferenceUrl = normalizeUrlForDraft(
    payload.generateImgReferenceUrl
  )

  return payload
}

export function parseDraftPayload(payloadJson: unknown): WebDraftPayload {
  if (typeof payloadJson === 'string') {
    try {
      return JSON.parse(payloadJson) as WebDraftPayload
    } catch {
      return { draft_source: 'web' }
    }
  }
  if (payloadJson && typeof payloadJson === 'object') {
    return payloadJson as WebDraftPayload
  }
  return { draft_source: 'web' }
}

export function adaptPayloadToWeb(payload: WebDraftPayload): WebDraftPayload {
  if (payload?.draft_source === 'web') return payload

  const next: WebDraftPayload = { draft_source: 'web' }
  WEB_DRAFT_FIELDS.forEach((key) => {
    next[key] = payload[key]
  })

  next.charNickname = typeof payload.name === 'string' ? payload.name : ''
  next.charGender = payload.gender ?? null
  next.character_type =
    normalizeCharacterType(payload.character_type) ??
    normalizeCharacterType(payload.characterType) ??
    1
  next.role_character_ids =
    normalizeStringArray(payload.role_character_ids) ??
    normalizeStringArray(payload.character_ids) ??
    normalizeStringArray(payload.roleCharacterIds) ??
    normalizeStringArray(payload.characterIds) ??
    []
  next.role_character_list =
    normalizeIdList(payload.role_character_list) ??
    normalizeIdList(payload.role_character_list_data) ??
    normalizeIdList(payload.id_list) ??
    []
  next.plot_file_key =
    typeof payload.plot_file_key === 'string'
      ? payload.plot_file_key
      : typeof payload.plotFileKey === 'string'
        ? payload.plotFileKey
        : ''
  next.plot_file_name =
    typeof payload.plot_file_name === 'string'
      ? payload.plot_file_name
      : typeof payload.plotFileName === 'string'
        ? payload.plotFileName
        : ''
  next.plot_file_prefix =
    typeof payload.plot_file_prefix === 'string'
      ? payload.plot_file_prefix
      : typeof payload.plotFilePrefix === 'string'
        ? payload.plotFilePrefix
        : ''
  next.is_plot_file_uploading =
    typeof payload.is_plot_file_uploading === 'boolean'
      ? payload.is_plot_file_uploading
      : typeof payload.isPlotFileUploading === 'boolean'
        ? payload.isPlotFileUploading
        : false
  next.user_role_name =
    typeof payload.user_role_name === 'string' ? payload.user_role_name : ''
  next.user_role_profile =
    typeof payload.user_role_profile === 'string'
      ? payload.user_role_profile
      : ''
  next.user_role_image =
    typeof payload.user_role_image === 'string' ? payload.user_role_image : ''
  next.user_role_image_url =
    typeof payload.user_role_image_url === 'string'
      ? payload.user_role_image_url
      : ''
  next.user_role_face_url =
    typeof payload.user_role_face_url === 'string'
      ? payload.user_role_face_url
      : ''
  next.charShortDescribe =
    typeof payload.tagline === 'string' ? payload.tagline : ''
  next.charLongDescribe =
    typeof payload.personality === 'string' ? payload.personality : ''
  next.showCharLongDescribe =
    typeof payload.showPersonality === 'boolean'
      ? payload.showPersonality
      : false
  next.charGreeting =
    typeof payload.greeting === 'string' ? payload.greeting : ''
  next.charIsPublic =
    typeof payload.isPublic === 'string'
      ? payload.isPublic
      : typeof payload.isPublic === 'boolean'
        ? String(payload.isPublic)
        : ''
  next.nsfwCheck = typeof payload.rating === 'boolean' ? payload.rating : false
  next.charTagIDs = Array.isArray(payload.tags) ? payload.tags : []
  next.conversationStyleKey =
    typeof payload.conversationStyle === 'number'
      ? payload.conversationStyle
      : 0
  next.charBaseTone =
    typeof payload.voiceId === 'string' ? payload.voiceId : null
  next.charVoiceSpeed =
    typeof payload.voiceSpeed === 'number' ? payload.voiceSpeed : 1
  next.contentType =
    payload.contentType === 3 ||
    payload.contentType === 2 ||
    payload.contentType === 1
      ? payload.contentType
      : 1
  next.optimizationCode =
    typeof payload.optimizationCode === 'string' ? payload.optimizationCode : ''
  next.outputFormatConfig =
    typeof payload.outputFormatConfig === 'string'
      ? payload.outputFormatConfig
      : ''
  const customPrompt =
    payload.custom_prompt && typeof payload.custom_prompt === 'object'
      ? (payload.custom_prompt as Record<string, unknown>)
      : payload.customPrompt && typeof payload.customPrompt === 'object'
        ? (payload.customPrompt as Record<string, unknown>)
        : undefined
  next.replyRequirement =
    typeof payload.replyRequirement === 'string'
      ? payload.replyRequirement
      : typeof customPrompt?.reply_requirement === 'string'
        ? customPrompt.reply_requirement
        : typeof customPrompt?.replyRequirement === 'string'
          ? customPrompt.replyRequirement
          : ''

  const dialogStyle =
    (payload.charDialogStyle as Array<{ role: string; message: string }>) || []
  if (!dialogStyle.length && typeof payload.exampleDialogues === 'string') {
    next.charDialogStyle = [
      { role: 'assistant', message: '' },
      { role: 'user', message: '' },
      { role: 'all', message: payload.exampleDialogues },
    ]
  }

  next.charImageUrl = typeof payload.image === 'string' ? payload.image : ''
  next.charImagePath =
    typeof payload.imageKey === 'string' ? payload.imageKey : ''
  next.charFaceImagePreviewUrl =
    typeof payload.face === 'string' ? payload.face : ''
  next.charFaceImagePath =
    typeof payload.faceKey === 'string' ? payload.faceKey : ''

  next.uploadUploadImageUrl =
    typeof payload.uploadImage === 'string' ? payload.uploadImage : ''
  next.uploadUploadImageKey =
    typeof payload.uploadImageKey === 'string'
      ? payload.uploadImageKey
      : undefined

  next.uploadGifKey =
    typeof payload.gifImageKey === 'string' ? payload.gifImageKey : undefined
  next.uploadGifUrl =
    typeof payload.gifImage === 'string' ? payload.gifImage : undefined
  next.greetingVideoKey =
    typeof payload.greetingVideoKey === 'string'
      ? payload.greetingVideoKey
      : undefined
  next.greetingVideoUrl =
    typeof payload.greetingVideoUrl === 'string'
      ? payload.greetingVideoUrl
      : typeof payload.greetingVideoUri === 'string'
        ? payload.greetingVideoUri
        : undefined

  return next
}

export function toWebStorePatch(
  payload: WebDraftPayload
): Partial<CreateCharForm> {
  const patch: Partial<CreateCharForm> = {}
  WEB_DRAFT_FIELDS.forEach((key) => {
    ;(patch as Record<string, unknown>)[key] = payload[key]
  })

  patch.createImgStep ??= 'uploadImg'
  patch.uploadImgReferencePreviewUrl ??= null
  patch.uploadImgReferencePath ??= null
  patch.uploadGifReferencePreviewUrl ??= null
  patch.uploadGifReferencePath ??= null
  patch.showUploadGif ??= false
  patch.generateImgReferencePreviewUrl ??= null
  patch.generateImgReferencePath ??= null
  patch.generateImgReferenceUrl ??= null
  patch.generatedNum ??= 4
  patch.imgFaceReferenceCroppedArea ??= null
  patch.imgFaceReferenceCroppedAreaPixels ??= null
  patch.isMimicFace ??= false
  patch.isMimicStyle ??= false
  patch.isMimicImage ??= false
  patch.imgPrompt ??= ''
  patch.imgStyleKey ??= null
  patch.imgStylesKey ??= []
  patch.uploadImgType ??= 'Upload'
  patch.uploadUploadImageKey ??= undefined
  patch.uploadUploadImageUrl ??= undefined
  patch.uploadUpscaleImageKey ??= undefined
  patch.uploadUpscaleImageUrl ??= undefined
  patch.uploadVaryImageKey ??= undefined
  patch.uploadVaryImageUrl ??= undefined
  patch.uploadGifKey ??= undefined
  patch.uploadGifUrl ??= undefined
  patch.croppedArea ??= null
  patch.croppedAreaPixels ??= null
  patch.generatingImgData ??= [null, null, null, null]
  patch.charImgSourcePath ??= undefined
  patch.sourceImgReferencePreviewUrl ??= null
  patch.sourceImgPrompt ??= null
  patch.charImageUrl ??= ''
  patch.charImagePath ??= ''
  patch.charPcImageUrl ??= ''
  patch.charPcImagePath ??= null
  patch.charFaceImageCroppedArea ??= null
  patch.charFaceImageCroppedAreaPixels ??= null
  patch.charFaceImagePreviewUrl ??= ''
  patch.charFaceImagePath ??= ''
  patch.nsfwCheck ??= false
  patch.charNickname ??= ''
  patch.charGender ??= null
  patch.character_type ??= 1
  patch.role_character_ids ??= []
  patch.role_character_list ??= []
  patch.plot_file_key ??= ''
  patch.plot_file_name ??= ''
  patch.plot_file_prefix ??= ''
  patch.is_plot_file_uploading ??= false
  // 旧草稿（无 plot_file_loaded* 字段）恢复后两者取默认值：
  // loaded=false 会让共享 echo effect 重新拉取服务端剧本绑定并修复表单，
  // 避免上面的 ??= '' 把已回显/已绑定的剧本静默清掉。
  patch.plot_file_loaded =
    typeof payload.plot_file_loaded === 'boolean'
      ? payload.plot_file_loaded
      : false
  patch.plot_file_loaded_character_id =
    typeof payload.plot_file_loaded_character_id === 'string'
      ? payload.plot_file_loaded_character_id
      : ''
  patch.world_books = normalizeWorldBooks(payload.world_books)
  patch.world_books_loaded =
    typeof payload.world_books_loaded === 'boolean'
      ? payload.world_books_loaded
      : false
  patch.world_books_loaded_character_id =
    typeof payload.world_books_loaded_character_id === 'string'
      ? payload.world_books_loaded_character_id
      : ''
  patch.charShortDescribe ??= ''
  patch.charLongDescribe ??= ''
  patch.showCharLongDescribe ??= false
  patch.charGreeting ??= ''
  patch.charTagIDs ??= []
  patch.charScenario ??= ''
  patch.advancedSetCheck ??= false
  patch.conversationStyleKey ??= 0
  patch.charDialogStyle ??= [
    { role: 'assistant', message: '' },
    { role: 'user', message: '' },
  ]
  patch.charBaseTone ??= null
  patch.charVoiceSpeed ??= 1
  patch.greetingVideoKey ??= undefined
  patch.greetingVideoUrl ??= undefined
  patch.charIsPublic ??= ''
  patch.contentType ??= 1
  patch.optimizationCode ??= ''
  patch.outputFormatConfig ??= ''
  patch.replyRequirement ??= ''

  return patch
}
